import { useEffect, useMemo } from 'react';
import { metres, type Spot } from '@day';
import { createStore, useStore } from './store';
import { crossings, shareOut, type Balance, type LeftWhy, type ShareInput, type ShareResult } from '../data/share';
import { defaultCapacity } from '../data/runs';
import { spotOf, type Waiting } from '../data/waiting';
import { zonesAt } from '../data/zones';
import { straight, type Timed } from '../data/routing';
import type { Model, Person } from '../data/model';

/**
 * Sharing waiting stops out across couriers on Live (src/pages/Share.tsx), around the engine (src/data/share.ts): what the
 * owner chose, the engine's answer worked out off the page's thread, and the routes as shown, with the owner's own moves
 * and Google's order (Sort all) laid over it. Nothing here is stored: Google's positions stay in memory and on Google's
 * map until a route is sent, as any route is.
 */

export interface Share {
  /** The waiting stops being shared out. */
  ids: string[];
  /** The couriers picked, in the owner's order. */
  couriers: string[];
  /** As few as needed: Routr picks from [couriers] only as many as the stops need. */
  fewest: boolean;
  balance: Balance;
  /** Most stops each, as the owner set it; unset: what their vehicle carries (runs.dart `defaultCapacity`). */
  max: Record<string, number>;
  /** Stops the owner moved by hand: stop id to courier. */
  moved: Record<string, string>;
  /** Google's order and times for a courier's route (Sort all), for exactly the stops it had then. */
  sorted: Record<string, Timed>;
  /** The courier whose stops are open in the list. */
  open: string | null;
}

/** A new share of [ids]: everyone picked, Even stops. */
export const newShare = (ids: string[], couriers: string[]): Share => ({ ids, couriers, fewest: false, balance: 'stops', max: {}, moved: {}, sorted: {}, open: null });

/** A courier's most stops: as the owner set it, else what their vehicle carries. */
export const mostOf = (share: Share, p: Person) => share.max[p.uid] ?? defaultCapacity(p.courier.vehicle);

/** The engine's input from the model: the stops on the map, the couriers picked with their most, the zones, the start. */
export function inputOf(m: Model, share: Share): { input: ShareInput; stops: Waiting[]; unplaced: Waiting[] } {
  const stops = share.ids.map((id) => m.waiting.find((w) => w.id === id)).filter((w): w is Waiting => !!w);
  const placed = stops.filter((w) => !!spotOf(w));
  // A paused courier can't be sent anything: never shared to, whatever was ticked.
  const people = share.couriers.map((uid) => m.person(uid)).filter((p): p is Person => !!p && !p.paused);
  return {
    stops,
    unplaced: stops.filter((w) => !spotOf(w)),
    input: {
      stops: placed.map((w) => ({ id: w.id, ...spotOf(w)!, pickupId: typeof w.stop.pickupId === 'string' ? w.stop.pickupId : null, allowed: w.couriers })),
      couriers: people.map((p) => ({ uid: p.uid, max: mostOf(share, p) })),
      fewest: share.fewest,
      balance: share.balance,
      zones: m.zones.map((z) => ({ id: z.id, name: z.name, couriers: z.couriers, path: z.path })),
      start: m.shop ? { lat: m.shop.lat, lng: m.shop.lng } : null,
    },
  };
}

// ── The engine, off the page's thread ────────────────────────────────────

const engine = createStore<{ key: string; result: ShareResult | null }>({ key: '', result: null });
let worker: Worker | null | undefined;
let seq = 0;
const asked = new Map<number, string>();

function workerOf(): Worker | null {
  if (worker !== undefined) return worker;
  try {
    worker = new Worker(new URL('../data/share.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<{ seq: number; result: ShareResult }>) => {
      const key = asked.get(e.data.seq);
      asked.delete(e.data.seq);
      if (key != null && key === want) engine.set({ key, result: e.data.result });
    };
  } catch {
    worker = null;
  }
  return worker;
}

let want = '';

/** The engine's answer for [input]: null while it's being worked out. */
export function useEngine(input: ShareInput): ShareResult | null {
  const key = useMemo(() => JSON.stringify(input), [input]);
  const e = useStore(engine);
  useEffect(() => {
    want = key;
    if (engine.get().key === key) return;
    const w = workerOf();
    if (!w) return engine.set({ key, result: shareOut(input) });
    asked.set(++seq, key);
    w.postMessage({ seq, input });
  }, [key]);
  return e.key === key ? e.result : null;
}

// ── The routes as shown ──────────────────────────────────────────────────

export interface Shown {
  uid: string;
  p: Person;
  /** Stop ids in the order they're visited. */
  ids: string[];
  distanceM: number;
  drivingS: number;
  /** Driving and 3 minutes at each stop. */
  totalS: number;
  /** Google's order and times (Sort all), or straight lines at 30 km/h. */
  google: boolean;
  /** Stops the owner moved here against a zone or the couriers they name: stop id to why. */
  warn: Map<string, string>;
}

export interface LeftOut {
  w: Waiting;
  why: LeftWhy | { kind: 'unplaced' };
}

export interface SharedView {
  routes: Shown[];
  left: LeftOut[];
  /** How many times two couriers' routes cross (the legs from the start counted). */
  crossings: number;
  /** Where each stop is, by id. */
  at: Map<string, Spot>;
  stops: Waiting[];
}

const DWELL_S = 180;

/** A route's numbers by straight lines from the start, in the order given (routing.ts `straight`). */
function numbers(start: Spot | null, ids: string[], at: Map<string, Spot>) {
  const t = straight(start, ids.filter((id) => at.has(id)).map((id) => ({ id, ...at.get(id)! })));
  return { distanceM: t.distanceM, drivingS: t.drivingS, totalS: t.drivingS + ids.length * DWELL_S };
}

/** Where [id] fits best in [ids]: the gap that adds least, by straight lines (actions.ts `fitIndex`). */
function fit(start: Spot | null, ids: string[], at: Map<string, Spot>, x: Spot): number {
  let best = ids.length;
  let cost = Infinity;
  for (let i = 0; i <= ids.length; i++) {
    const a = i === 0 ? start : at.get(ids[i - 1]) ?? null;
    const b = i < ids.length ? at.get(ids[i]) ?? null : null;
    const c = (a ? metres(a, x) : 0) + (b ? metres(x, b) - (a ? metres(a, b) : 0) : 0);
    if (c < cost) {
      cost = c;
      best = i;
    }
  }
  return best;
}

/** Why a courier isn't one a stop's zones allow, or null when they are (or it's in no zone with couriers). */
export function zoneWarning(m: Model, at: Spot, uid: string): string | null {
  const zs = zonesAt(at, m.zones).filter((z) => z.couriers.length);
  if (!zs.length || zs.some((z) => z.couriers.includes(uid))) return null;
  const who = m.person(uid)?.first ?? 'They';
  return `In ${zs.map((z) => z.name).join(' and ')}’s zone, which ${who} doesn’t cover`;
}

/** The couriers a stop names, in words: "Musa", "Musa or Tunde", "Musa, Tunde or Bayo"; "someone who has left" for none still here. */
export function whoMay(m: Model, uids: string[]): string {
  const xs = uids.map((u) => m.person(u)?.first).filter((x): x is string => !!x);
  if (!xs.length) return uids.length === 1 ? 'a courier who has left' : 'couriers who have left';
  return xs.length === 1 ? xs[0] : `${xs.slice(0, -1).join(', ')} or ${xs.at(-1)}`;
}

/**
 * Of couriers still in the business who could take a left-out stop, those picking them would let ([free]), and in words who
 * isn't picked today and who is paused: "Musa isn’t picked today, and Tunde is paused". A paused one can't be picked here.
 */
export function notPicked(m: Model, uids: string[]): { free: string[]; words: string } {
  const list = (xs: string[]) => {
    const names = xs.map((u) => m.person(u)?.first ?? 'a courier');
    return names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
  };
  const free = uids.filter((u) => !m.person(u)?.paused);
  const paused = uids.filter((u) => m.person(u)?.paused);
  const words = [
    free.length ? `${list(free)} ${free.length === 1 ? 'isn’t' : 'aren’t'} picked today` : '',
    paused.length ? `${list(paused)} ${paused.length === 1 ? 'is' : 'are'} paused` : '',
  ].filter(Boolean).join(', and ');
  return { free, words };
}

/** Why [uid] isn't one a waiting stop names as allowed to take it, or null when they are (or it names no one). */
export function allowedWarning(m: Model, w: Pick<Waiting, 'couriers'> | undefined, uid: string): string | null {
  if (!w?.couriers?.length || w.couriers.includes(uid)) return null;
  return `Only ${whoMay(m, w.couriers)} may take it`;
}

/** Why [uid] isn't one a stop's rules allow: the couriers it names first, then its zones. */
export const ruleWarning = (m: Model, w: Waiting | undefined, at: Spot, uid: string) => allowedWarning(m, w, uid) ?? zoneWarning(m, at, uid);

/**
 * The engine's routes with the owner's own moves laid over them (each stop where it fits best on its new route; a pickup
 * and its delivery move together; a move against a zone, or to a courier a stop doesn't name, is kept, with a warning),
 * then Google's order and times for a
 * route Sort all asked about, while its stops are still the ones it had.
 */
export function shown(m: Model, share: Share, result: ShareResult, stops: Waiting[], unplaced: Waiting[]): SharedView {
  const at = new Map<string, Spot>();
  for (const w of stops) {
    const s = spotOf(w);
    if (s) at.set(w.id, s);
  }
  const start = m.shop ? { lat: m.shop.lat, lng: m.shop.lng } : null;
  const byId = new Map(stops.map((w) => [w.id, w]));
  const lists = new Map<string, string[]>(result.routes.map((r) => [r.uid, [...r.ids]]));
  const left = new Map(result.left.map((x) => [x.id, x.why]));
  const warn = new Map<string, Map<string, string>>();
  // A pickup and the deliveries it feeds move together.
  const partners = (id: string) => {
    const w = byId.get(id);
    const pickup = typeof w?.stop.pickupId === 'string' ? w.stop.pickupId : id;
    return [pickup, ...stops.filter((x) => x.stop.pickupId === pickup).map((x) => x.id)].filter((x, i, all) => byId.has(x) && all.indexOf(x) === i);
  };
  for (const [id, uid] of Object.entries(share.moved)) {
    if (!at.has(id) || !m.person(uid)) continue;
    const group = partners(id);
    for (const [, ids] of lists) for (const g of group) ids.includes(g) && ids.splice(ids.indexOf(g), 1);
    for (const g of group) left.delete(g);
    const into = lists.get(uid) ?? [];
    lists.set(uid, into);
    for (const g of group) {
      into.splice(fit(start, into, at, at.get(g)!), 0, g);
      const why = ruleWarning(m, byId.get(g), at.get(g)!, uid);
      if (why) warn.set(uid, (warn.get(uid) ?? new Map()).set(g, why));
    }
    // The pickup first.
    const p = group[0];
    const di = into.findIndex((x) => group.slice(1).includes(x));
    if (di >= 0 && into.indexOf(p) > di) {
      into.splice(into.indexOf(p), 1);
      into.splice(di, 0, p);
    }
  }
  // The couriers picked keep their order; one moved to by hand who wasn't picked comes after them.
  const order = [...share.couriers, ...[...lists.keys()].filter((u) => !share.couriers.includes(u))];
  const routes: Shown[] = order
    .filter((uid) => lists.has(uid) && m.person(uid))
    .map((uid) => {
      const ids = lists.get(uid)!;
      const g = share.sorted[uid];
      const same = !!g && g.order.length === ids.length && g.order.every((id) => ids.includes(id));
      const n = same ? { distanceM: g!.distanceM, drivingS: g!.drivingS, totalS: g!.drivingS + ids.length * DWELL_S } : numbers(start, ids, at);
      return { uid, p: m.person(uid)!, ids: same ? [...g!.order] : ids, ...n, google: same && g!.method === 'routesApi', warn: warn.get(uid) ?? new Map() };
    })
    // As few as needed: a courier with nothing isn't one of them.
    .filter((r) => !share.fewest || r.ids.length);
  return {
    routes,
    left: [...unplaced.map((w) => ({ w, why: { kind: 'unplaced' as const } })), ...[...left].flatMap(([id, why]) => (byId.has(id) ? [{ w: byId.get(id)!, why }] : []))],
    crossings: crossings(routes, (id) => at.get(id), start),
    at,
    stops,
  };
}

/** The view as last drawn, for the map, menus and ⌘K (they run outside the list's React tree). */
export const sharedNow = createStore<{ view: SharedView | null }>({ view: null });
